import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Plus, Volume2, VolumeX } from "lucide-react";
import { playZip, toggleSound, isSoundOn } from "@/lib/zipSound";

const LINKS = [
    { to: "/", label: "Home", id: "home" },
    { to: "/shop", label: "Collection", id: "shop" },
    { to: "/about", label: "About", id: "about" },
    { to: "/contact", label: "Contact", id: "contact" },
    { to: "/account", label: "Account", id: "account" },
];

/* A copper jeans rivet that doubles as a quick-navigation menu. */
export default function RivetDock() {
    const [open, setOpen] = useState(false);
    const [snd, setSnd] = useState(isSoundOn());
    const { pathname } = useLocation();
    useEffect(() => setOpen(false), [pathname]);
    return (
        <div data-testid="rivet-dock" className="fixed bottom-5 right-5 z-[65] flex flex-col items-end gap-3">
            <AnimatePresence>
                {open &&
                    LINKS.map((l, i) => (
                        <motion.div
                            key={l.id}
                            initial={{ opacity: 0, x: 26 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 26 }}
                            transition={{
                                delay: open ? i * 0.05 : 0,
                                duration: 0.35,
                                ease: [0.16, 1, 0.3, 1],
                            }}
                        >
                            <Link to={l.to} data-testid={`rivet-dock-link-${l.id}`} className="group flex items-center gap-3">
                                <span className="border border-gold/30 bg-black/85 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-ink backdrop-blur transition-colors group-hover:border-gold group-hover:text-gold">
                                    {l.label}
                                </span>
                                <span
                                    className={`rivet grid h-9 w-9 place-items-center rounded-full ${
                                        pathname === l.to ? "ring-2 ring-gold/60 ring-offset-2 ring-offset-obsidian" : ""
                                    }`}
                                >
                                    <span className="relative z-10 h-1.5 w-1.5 rounded-full bg-[#3c1e06]" />
                                </span>
                            </Link>
                        </motion.div>
                    ))}
            </AnimatePresence>
            <button
                data-testid="sound-toggle"
                onClick={() => {
                    const on = toggleSound();
                    setSnd(on);
                    if (on) playZip({ rate: 1.4, volume: 0.08 });
                }}
                aria-label={snd ? "Mute sounds" : "Enable sounds"}
                className="rivet grid h-10 w-10 place-items-center rounded-full"
            >
                {snd ? (
                    <Volume2 size={15} className="relative z-10 text-[#3c1e06]" />
                ) : (
                    <VolumeX size={15} className="relative z-10 text-[#3c1e06]" />
                )}
            </button>
            <button
                data-testid="rivet-dock-toggle"
                onClick={() => {
                    setOpen(!open);
                    playZip({ rate: open ? 1.3 : 1, volume: 0.08 });
                }}
                aria-label="Quick menu"
                className="rivet grid h-14 w-14 place-items-center rounded-full"
            >
                <Plus
                    size={22}
                    className={`relative z-10 text-[#3c1e06] transition-transform duration-300 ${open ? "rotate-45" : ""}`}
                />
            </button>
        </div>
    );
}
